import React, { useEffect, useMemo, useState, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, LabelList, Treemap,
} from 'recharts';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Layers, ChevronLeft, MapPin, FileDown, Loader2, IndianRupee, Eye } from 'lucide-react';
import { API, errorText } from '@/utils/api';
import ProductMixBreakdown from '@/components/ProductMixBreakdown';
import ProductMixAccountReport from '@/components/ProductMixAccountReport';
import ProductMixCompare, { SidePicker, KindSwitch, useAccountOptions, accountOptions } from '@/components/ProductMixCompare';

const ACCOUNT_KINDS = [{ key: 'customer', one: 'Customer' }, { key: 'store', one: 'Store' }];

// ─── Constants ──────────────────────────────────────────────
// 24 colours so up to ~30 qualities stay tellable apart on one pie.
const TRADE_COLORS = [
  '#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#3B82F6', '#EC4899', '#14B8A6',
  '#84CC16', '#F97316', '#06B6D4', '#A855F7', '#22C55E', '#EAB308', '#DC2626', '#0EA5E9',
  '#D946EF', '#65A30D', '#FB7185', '#2563EB', '#CA8A04', '#059669', '#7C3AED', '#E11D48',
];
// Chart preferences for every "Product mix" chart on the page — set once in
// the toolbar, remembered per browser (same keys/rules as Orders & Dispatch).
const PRODUCT_CHART_TYPES = [
  { key: 'pie', label: 'Pie' }, { key: 'donut', label: 'Donut' }, { key: 'bars', label: 'Bars' }, { key: 'treemap', label: 'Treemap' },
];
const readPref = (k, fallback) => { try { return localStorage.getItem(k) || fallback; } catch { return fallback; } };
const writePref = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode etc. */ } };
const ChartPrefs = createContext({ chartType: 'donut', topChoice: 'auto', division: 'all' });

// Where a quality's fabric is set (Settings → Fabric Rules), opened on this division.
const FabricRulesLink = ({ q }) => {
  const { division, memberView } = useContext(ChartPrefs);
  if (memberView) return null;   // Settings is admin + accounts; a sales member can't open it
  const params = new URLSearchParams({ tab: 'trade_rules' });
  if (division && division !== 'all') params.set('division', division);
  if (q) params.set('q', q);
  return (
    <Link to={`/settings?${params.toString()}`} onClick={(e) => e.stopPropagation()}
      className="text-[11px] font-normal text-indigo-600 hover:underline whitespace-nowrap"
      title="Set which fabric each quality belongs to — Settings → Fabric Rules. Product Mix and the customer report both follow these rules.">
      Fabric rules →
    </Link>
  );
};

const _LEGACY_TRADE_COLORS = [
  '#264478', '#7C3AED', '#0EA5E9', '#16A34A', '#F59E0B',
  '#EC4899', '#64748B', '#9333EA', '#0891B2', '#DC2626',
];

const fmtCurrency = (v) => {
  if (v == null || isNaN(v)) return '₹0';
  const a = Math.abs(v);
  if (a >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (a >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

const fmtNumber = (v) => {
  if (v == null || isNaN(v)) return '0';
  return Math.round(v).toLocaleString('en-IN');
};

// ─── FY helpers ─────────────────────────────────────────────
const getCurrentFY = () => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};
const fyLabel = (y) => `FY ${y}-${String(y + 1).slice(-2)}`;
const fyMonthOptions = (y) => [
  { v: `${y}-04`, l: `Apr ${y}` },   { v: `${y}-05`, l: `May ${y}` },
  { v: `${y}-06`, l: `Jun ${y}` },   { v: `${y}-07`, l: `Jul ${y}` },
  { v: `${y}-08`, l: `Aug ${y}` },   { v: `${y}-09`, l: `Sep ${y}` },
  { v: `${y}-10`, l: `Oct ${y}` },   { v: `${y}-11`, l: `Nov ${y}` },
  { v: `${y}-12`, l: `Dec ${y}` },
  { v: `${y + 1}-01`, l: `Jan ${y + 1}` },
  { v: `${y + 1}-02`, l: `Feb ${y + 1}` },
  { v: `${y + 1}-03`, l: `Mar ${y + 1}` },
];

// Complete order + dispatch data starts 1 Apr 2026 (Saurav, 2026-10-28), so
// earlier years are not offered. Derived up to the current FY, so April never
// silently drops the new year from the list.
const FIRST_COMPLETE_FY = 2026;
const FY_OPTIONS = Array.from({ length: Math.max(1, getCurrentFY() - FIRST_COMPLETE_FY + 1) }, (_, i) => FIRST_COMPLETE_FY + i).map(y => ({ value: y, label: fyLabel(y) }));
const QUARTERS = [
  { v: 1, l: 'Q1 (Apr–Jun)' }, { v: 2, l: 'Q2 (Jul–Sep)' },
  { v: 3, l: 'Q3 (Oct–Dec)' }, { v: 4, l: 'Q4 (Jan–Mar)' },
];

// ─── Tooltips ───────────────────────────────────────────────
const PieTip = ({ active, payload, metric }) => {
  if (!active || !payload?.length) return null;
  const e = payload[0];
  const d = e.payload;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2.5 text-sm">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: e.payload.color }} />
        <span className="font-semibold text-gray-800">{e.name}</span>
      </div>
      <div className="text-xs text-gray-600">
        {metric === 'value'
          ? <>Value: <span className="font-semibold text-gray-900">{fmtCurrency(d.value)}</span></>
          : <>Qty: <span className="font-semibold text-gray-900">{fmtNumber(d.value)} {d.qtyUnit}</span></>
        }
      </div>
      <div className="text-xs text-gray-500 mt-0.5">{d.pct.toFixed(2)}% contribution</div>
    </div>
  );
};


// ─── Fabric-type stack (TAS only: Shirting / Suiting / Ethnic / Other) ──
const FABRIC_ORDER = ['Shirting', 'Suiting', 'Ethnic', 'Other', 'Unclear'];
const FABRIC_COLORS = {
  Shirting: '#0EA5E9',  // sky
  Suiting:  '#7C3AED',  // violet
  Ethnic:   '#EC4899',  // pink
  Other:    '#64748B',  // slate
  Unclear:  '#F59E0B',  // amber
};

// One composition ({name, value, pct, color}) in the toolbar's chart shape —
// so every chart on the page answers to the same Chart switch.
const ShareChart = ({ data, chartType, height = 260, tip, inner = 38, outer = 70, labelMin = 4 }) => {
  if (chartType === 'bars') {
    return (
      <ResponsiveContainer width="100%" height={Math.max(height * 0.8, data.length * 24 + 30)}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 60, left: 4, bottom: 4 }}>
          <CartesianGrid horizontal={false} stroke="#F1F5F9" />
          <XAxis type="number" tick={{ fontSize: 10, fill: '#64748B' }} unit="%" domain={[0, 'auto']} dataKey="pct" />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} width={150} interval={0} />
          {tip}
          <Bar isAnimationActive={false} dataKey="pct" radius={[0, 3, 3, 0]}>
            {data.map((e, i) => <Cell key={i} fill={e.color} />)}
            <LabelList dataKey="pct" position="right" formatter={(v) => `${Number(v).toFixed(1)}%`} style={{ fontSize: 9, fill: '#334155', fontWeight: 600 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (chartType === 'treemap') {
    const TreeCell = ({ x, y, width, height: h, name, pct, color }) => {
      if (width == null || h == null) return null;
      const roomy = width > 64 && h > 30;
      const maxChars = Math.max(3, Math.floor(width / 6.5));
      return (
        <g>
          <rect x={x} y={y} width={width} height={h} rx={3} fill={color || '#CBD5E1'} stroke="#fff" strokeWidth={2} />
          {roomy && <text x={x + 5} y={y + 14} fontSize={10} fontWeight={600} fill="#fff">{(name || '').length > maxChars ? `${(name || '').slice(0, maxChars - 1)}…` : name}</text>}
          {roomy && h > 44 && pct != null && <text x={x + 5} y={y + 27} fontSize={9} fill="rgba(255,255,255,0.9)">{Number(pct).toFixed(1)}%</text>}
        </g>
      );
    };
    return (
      <ResponsiveContainer width="100%" height={height}>
        <Treemap data={data.map(e => ({ ...e, size: Math.max(0, e.value || 0) }))} dataKey="size" nameKey="name" aspectRatio={4 / 3} isAnimationActive={false} content={<TreeCell />}>
          {tip}
        </Treemap>
      </ResponsiveContainer>
    );
  }
  const donut = chartType !== 'pie';
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
        <Pie isAnimationActive={false} data={data} dataKey="value" nameKey="name"
          innerRadius={donut ? inner : 0} outerRadius={outer} paddingAngle={donut ? 1.5 : 0}
          stroke="#fff" strokeWidth={1.5}
          label={(p) => p.pct >= labelMin ? `${p.name.length > 12 ? p.name.substring(0, 12) + '…' : p.name} ${p.pct.toFixed(1)}%` : ''}
          labelLine={{ stroke: '#94A3B8', strokeWidth: 0.8 }}>
          {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
        </Pie>
        {tip}
      </PieChart>
    </ResponsiveContainer>
  );
};

// ─── Twin Pie Analysis ────────────────────────────────────
// Two value-driven pies side-by-side per fabric:
//   Pie A — every price band (no Others, full price ladder).
//   Pie B — trades above an auto-relaxing threshold (start 2%, fall to
//           1% if the residual 'Others' would still exceed 40%).
// Both pies are sized BY ₹ VALUE — pieces alone is misleading because
// each piece varies in metres × per-metre rate.
const TwinPieAnalysis = ({ fabric, color, priceBands, orderTrades, summary, metric = 'value' }) => {
  // Price bands sized by the toolbar metric (₹ or metres)
  const byValue = metric === 'value';
  const pieA = useMemo(() => {
    const bands = (priceBands?.bands) || [];
    return bands
      .filter(b => ((byValue ? b.value : b.metres) || 0) > 0)
      .map((b, i) => ({
        name: b.band,
        value: byValue ? b.value : b.metres,
        pct: (byValue ? b.pct_value : b.pct_metres) || 0,
        color: TRADE_COLORS[i % TRADE_COLORS.length],
        topTrades: b.top_trades || [],
        units: b.units, lines: b.lines, metres: b.metres, rupees: b.value,
      }));
  }, [priceBands, byValue]);


  if (!pieA.length) return null;

  const renderTooltip = (kind) => ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2.5 text-sm">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
          <span className="font-semibold text-gray-800">{d.name}</span>
        </div>
        <div className="text-xs space-y-0.5">
          <div className="flex justify-between gap-4"><span className="text-gray-500">Value</span><span className="font-semibold">{fmtCurrency(d.rupees ?? d.value)}</span></div>
          <div className="flex justify-between gap-4"><span className="text-gray-500">% share</span><span className="font-semibold">{d.pct.toFixed(2)}%</span></div>
          {kind === 'A' && (
            <>
              <div className="flex justify-between gap-4"><span className="text-gray-500">Dispatch lines</span><span className="font-semibold">{fmtNumber(d.lines)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-500">Metres</span><span className="font-semibold">{fmtNumber(d.metres)}</span></div>
              {d.topTrades?.length > 0 && (
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <div className="text-gray-500 mb-0.5">Top trades:</div>
                  <div className="text-gray-700">{d.topTrades.slice(0, 3).map(t => t.trade_name).join(', ')}</div>
                </div>
              )}
            </>
          )}
          {kind === 'B' && d.tradeCount > 1 && (
            <div className="flex justify-between gap-4"><span className="text-gray-500">Trades clubbed</span><span className="font-semibold">{d.tradeCount}</span></div>
          )}
        </div>
      </div>
    );
  };

  // Custom slice label — show on slices ≥ 3% to avoid clutter
  const sliceLabel = (p) => {
    if (p.pct < 3) return '';
    const short = p.name.length > 12 ? p.name.substring(0, 12) + '…' : p.name;
    return `${short} ${p.pct.toFixed(1)}%`;
  };

  return (
    <Card className="border border-gray-200 shadow-sm bg-white">
      <CardHeader className="pb-3 border-b border-gray-100" style={{ borderLeft: `4px solid ${color}`, paddingLeft: 16 }}>
        <CardTitle className="flex items-center justify-between text-sm font-semibold text-gray-800">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}1A`, color }}>
              <IndianRupee size={13} />
            </span>
            {fabric} — composition by {byValue ? 'value' : 'metres'}
          </div>
          <div className="text-[11px] font-normal text-gray-500">
            Total dispatched {fmtCurrency(summary?.net_value || 0)}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-8">
        {/* Pie A — price bands (the only pie now; trade-orders pie was
            duplicating data already shown in the dual-pct bar below) */}
        {pieA.length > 0 && (
          <PieSection
            title="By price band"
            subtitle={`realised ₹/m after discounts, share of dispatched ${byValue ? '₹' : 'metres'}`}
            tooltip="Each slice is a ₹150-wide band of realised selling rate per metre (net value ÷ metres, after discounts), sized by the net ₹ dispatched in that band. A mix bale sits in the band its actual selling rate puts it in. Lines without metres are Unpriced."
            data={pieA}
            renderTooltip={renderTooltip('A')}
            sliceLabel={sliceLabel}
            fmtValue={byValue ? fmtCurrency : (v) => `${fmtNumber(v)} m`}
          />
        )}
      </CardContent>
    </Card>
  );
};

// ─── Pie section shared by both pies ─────────────────────────
// Full-width donut with a "playful" colored-tile legend grid below —
// each tile carries the slice colour, name, and % so the legend reads
// like a set of small product cards rather than a cramped list.
const PieSection = ({ title, subtitle, tooltip, data, renderTooltip, sliceLabel, fmtValue = fmtCurrency }) => {
  const { chartType } = useContext(ChartPrefs);
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="text-sm font-semibold text-gray-800">{title}</span>
        <span className="text-[11px] text-gray-500">— {subtitle}</span>
        <span
          className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gray-200 text-gray-600 text-[9px] font-bold cursor-help"
          title={tooltip}
        >i</span>
      </div>

      {/* Donut with outer leader-line callouts.
          Custom label renders ONLY for slices ≥3% so the chart stays
          uncluttered — small slices remain coloured but unlabelled, with
          the tile legend below filling in the detail. */}
      {(chartType === 'bars' || chartType === 'treemap') ? (
        <ShareChart data={data} chartType={chartType} height={380} tip={<RechartsTooltip content={renderTooltip} />} />
      ) : (
      <ResponsiveContainer width="100%" height={460}>
        <PieChart margin={{ top: 40, right: 80, bottom: 40, left: 80 }}>
          <Pie isAnimationActive={false}
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={chartType === 'pie' ? 0 : 75}
            outerRadius={140}
            paddingAngle={1.5}
            stroke="#fff"
            strokeWidth={2}
            label={(props) => {
              const { cx, cy, midAngle, outerRadius, payload } = props;
              if ((payload.pct || 0) < 3) return null;
              const RAD = Math.PI / 180;
              // Three-segment leader line:
              //   1) from slice edge straight outward (15px)
              //   2) horizontal bend (20px) toward the side
              //   3) text anchored to that side
              const r1 = outerRadius + 8;
              const r2 = outerRadius + 22;
              const sx = cx + Math.cos(-midAngle * RAD) * outerRadius;
              const sy = cy + Math.sin(-midAngle * RAD) * outerRadius;
              const mx = cx + Math.cos(-midAngle * RAD) * r1;
              const my = cy + Math.sin(-midAngle * RAD) * r1;
              const ex = cx + Math.cos(-midAngle * RAD) * r2;
              const ey = cy + Math.sin(-midAngle * RAD) * r2;
              const isRight = Math.cos(-midAngle * RAD) >= 0;
              const tx = ex + (isRight ? 4 : -4);
              const anchor = isRight ? 'start' : 'end';
              const shortName = payload.name.length > 20 ? payload.name.substring(0, 20) + '…' : payload.name;
              return (
                <g>
                  <path
                    d={`M${sx},${sy} L${mx},${my} L${ex},${ey}`}
                    stroke={payload.color}
                    fill="none"
                    strokeWidth={1}
                  />
                  <circle cx={ex} cy={ey} r={2.5} fill={payload.color} />
                  <text
                    x={tx}
                    y={ey - 1}
                    textAnchor={anchor}
                    style={{ fontSize: 11, fontWeight: 600, fill: '#1E293B' }}
                  >
                    {shortName}
                  </text>
                  <text
                    x={tx}
                    y={ey + 12}
                    textAnchor={anchor}
                    style={{ fontSize: 11, fontWeight: 700, fill: payload.color }}
                  >
                    {payload.pct.toFixed(1)}%
                  </text>
                </g>
              );
            }}
            labelLine={false}
          >
            {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
          <RechartsTooltip content={renderTooltip} />
        </PieChart>
      </ResponsiveContainer>
      )}

      {/* Coloured-tile legend — each tile is a card in the slice's colour */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-2">
        {data.map((d, i) => (
          <div
            key={i}
            className="rounded-lg px-3 py-2 border-l-[3px]"
            style={{
              borderLeftColor: d.color,
              backgroundColor: `${d.color}10`,
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold text-gray-800 truncate" title={d.name}>{d.name}</span>
              <span className="text-[11px] font-bold flex-shrink-0" style={{ color: d.color }}>{d.pct.toFixed(1)}%</span>
            </div>
            <div className="text-[10px] text-gray-600 mt-0.5">{fmtValue(d.value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Price-Band Card (TAS Shirting / Suiting only) ──────────────
// Bands ordered DESCENDING by ₹ value contribution per the user's spec.

const FabricBucketCard = ({ fabric, summary, dispatchTrades, orderTrades, metric }) => {
  // Filter trades down to this fabric bucket
  const dTrades = useMemo(() => (dispatchTrades || []).filter(t => (t.fabric_type || 'Other') === fabric), [dispatchTrades, fabric]);
  const oTrades = useMemo(() => (orderTrades || []).filter(t => (t.fabric_type || 'Other') === fabric), [orderTrades, fabric]);
  const color = FABRIC_COLORS[fabric] || '#64748B';

  // If both order & dispatch sides are empty for this fabric, hide the card.
  const isEmpty = (!summary || ((summary.metres || 0) === 0 && (summary.pieces || 0) === 0 && (summary.order_units || 0) === 0));
  if (isEmpty) return null;
  // Ethnic + Other + Unclear are summarised in the head category tile — no
  // separate per-fabric block is rendered (no per-trade composition was
  // actionable for these aggregate buckets).
  if (fabric === 'Ethnic' || fabric === 'Other' || fabric === 'Unclear') return null;

  const valueKey = metric === 'value' ? 'pct_value' : 'pct_qty';
  const rawKey = metric === 'value' ? 'value' : 'qty';

  // Recompute % within this fabric bucket so they sum to 100 inside the
  // block — each percentage against ITS OWN total. (It used to divide qty by
  // the total of whichever metric was selected, so with "By Value" on every
  // "% by units" read 0.0%.)
  const pctPair = (list) => {
    const qtyTotal = list.reduce((s, t) => s + (t.qty || 0), 0);
    const valueTotal = list.reduce((s, t) => s + (t.value || 0), 0);
    return list.map(t => ({
      ...t,
      pct_qty: qtyTotal ? ((t.qty || 0) / qtyTotal * 100) : 0,
      pct_value: valueTotal ? ((t.value || 0) / valueTotal * 100) : 0,
    }));
  };
  const dispRanked = pctPair(dTrades);
  const orderRanked = pctPair(oTrades);

  return (
    <Card className="border border-gray-200 shadow-sm">
      <CardHeader className="pb-3 border-b border-gray-100" style={{ borderLeft: `4px solid ${color}`, paddingLeft: 16 }}>
        <CardTitle className="flex items-center justify-between text-base font-semibold text-gray-800">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}1A`, color }}>
              <Layers size={15} />
            </span>
            {fabric}
          </div>
          <div className="text-xs font-normal text-gray-500 flex items-center gap-3">
            <FabricRulesLink />
            <span>{(summary.pct_metres ?? summary.pct_pieces)?.toFixed(1) || 0}% m</span>
            <span>·</span>
            <span>{summary.pct_value?.toFixed(1) || 0}% ₹</span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Ethnic + Other: no per-trade composition is shown — the head
            category tile at the top already conveys the relevant info
            (pieces + ₹ + % shares). Per-trade detail isn't actionable
            for these aggregate buckets (festive-wear, RTS packs,
            uniforms, mix bales). */}
        {fabric === 'Ethnic' || fabric === 'Other' ? null : (
          <>
            {/* Twin-pie analysis (price band + trades ≥ value threshold).
                Both pies are sized by ₹ value so the analyst sees the
                same revenue lens from two angles. */}
            <TwinPieAnalysis
              fabric={fabric}
              color={color}
              priceBands={summary?.price_bands}
              orderTrades={orderRanked}
              summary={summary}
              metric={metric}
            />
            {/* Price-band table removed — Pie A in TwinPieAnalysis above
                already conveys band-level demand visually. */}
            {/* Dispatches view — matches the PDF's trade-level dual-pct
                bar so the same trades surface in both renderings. Without
                this card, products like Vyff (heavy dispatches, near-zero
                orders) wouldn't appear on the web at all. */}
            <CompositionCard
              title="Dispatches"
              icon={Layers}
              color="#16A34A"
              orderTrades={orderRanked}
              summary={{
                qty: summary.metres || 0,
                pieces: summary.pieces || 0,
                value: summary.net_value || 0,
                tradeCount: dispRanked.length,
                metres: summary.metres || 0,
              }}
              trades={dispRanked}
              metric={metric}
              qtyLabel="Metres Dispatched"
              qtyUnit="m"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Head Fabric Category Overview ──────────────────────────
// Top-level visualization (pie + complementary bar of absolute values)
// shown ABOVE the per-fabric stack. Pie answers "what share?", bar
// answers "what magnitude?" — different lenses, no redundancy.
const HeadFabricTooltip = ({ active, payload, metric }) => {
  if (!active || !payload?.length) return null;
  const e = payload[0];
  const d = e.payload;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2.5 text-sm">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
        <span className="font-semibold text-gray-800">{e.name || d.name}</span>
      </div>
      <div className="text-xs text-gray-600">
        {metric === 'value'
          ? <>Value: <span className="font-semibold text-gray-900">{fmtCurrency(d.value)}</span></>
          : <>Metres: <span className="font-semibold text-gray-900">{fmtNumber(d.value)}</span></>
        }
      </div>
      <div className="text-xs text-gray-500 mt-0.5">{d.pct?.toFixed(2) || 0}% of total</div>
    </div>
  );
};

const HeadFabricCategoryCard = ({ breakdown, metric }) => {
  // All hooks must run unconditionally (rules-of-hooks). Bail out below.
  const { chartType } = useContext(ChartPrefs);
  const rows = useMemo(() => {
    if (!breakdown) return [];
    return FABRIC_ORDER
      .map(fabric => {
        const b = breakdown[fabric] || {};
        const value = metric === 'value' ? (b.net_value || 0) : (b.metres || 0);
        const pct = metric === 'value' ? (b.pct_value || 0) : (b.pct_metres ?? b.pct_pieces ?? 0);
        return {
          name: fabric,
          value,
          pct,
          pieces: b.pieces || 0,
          metres: b.metres || 0,
          netValue: b.net_value || 0,
          orderUnits: b.order_units || 0,
          orderValue: b.order_value || 0,
          color: FABRIC_COLORS[fabric],
        };
      })
      // Show only Shirting + Suiting in the head category tile / pie / bar.
      // Ethnic and Other are excluded per design — analytical focus is on
      // the two core fabric categories.
      .filter(r => (r.name === 'Shirting' || r.name === 'Suiting'))
      .filter(r => r.value > 0 || r.pieces > 0 || r.netValue > 0);
  }, [breakdown, metric]);

  // Bar plots BOTH % pieces AND % value side by side per fabric. Lets the
  // analyst spot fabrics where qty share and value share diverge — e.g. a
  // category with 30% of pieces but only 10% of ₹ is dragging blended
  // realised rate down, while one with 10% of pieces and 30% of ₹ is the
  // premium driver.
  const barRows = useMemo(() => {
    return rows.map(r => ({
      name: r.name,
      pctPieces: r.pct,  // r.pct already aligns with currently-selected metric — but we want both pcs & ₹ explicitly
      color: r.color,
      pieces: r.pieces,
      netValue: r.netValue,
      pctPcs: (breakdown[r.name] || {}).pct_metres ?? (breakdown[r.name] || {}).pct_pieces ?? 0,
      pctVal: (breakdown[r.name] || {}).pct_value || 0,
    }));
  }, [rows, breakdown]);

  if (!breakdown || !rows.length) return null;

  // Sub-total of just the categories shown in this card (Shirting + Suiting).
  const ssPieces = rows.reduce((s, r) => s + r.pieces, 0);
  const ssValue  = rows.reduce((s, r) => s + r.netValue, 0);

  // FULL dispatch total across ALL fabric categories — including Ethnic
  // and Other (Mixed Dispatch / Pack-Stock / Unknown). The card body only
  // breaks down Shirting + Suiting (analytical focus), but the header
  // should always show the total so it matches the dispatch report.
  const fullPieces = FABRIC_ORDER.reduce((s, f) => s + ((breakdown[f] || {}).pieces || 0), 0);
  const fullValue  = FABRIC_ORDER.reduce((s, f) => s + ((breakdown[f] || {}).net_value || 0), 0);
  const offPieces = fullPieces - ssPieces;
  const offValue  = fullValue  - ssValue;

  return (
    <Card className="border border-indigo-200 shadow-sm bg-gradient-to-br from-white to-indigo-50/30">
      <CardHeader className="pb-3 border-b border-indigo-100">
        <CardTitle className="flex items-center justify-between text-base font-semibold text-gray-800">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-md flex items-center justify-center bg-indigo-100 text-indigo-700">
              <Layers size={15} />
            </span>
            Head fabric category — overview
            <FabricRulesLink />
          </div>
          <div className="text-right">
            <div className="text-xs font-normal text-gray-500">
              <span className="font-semibold text-gray-700">Total dispatched:</span>{' '}
              {fmtNumber(fullPieces)} pcs · {fmtCurrency(fullValue)}
            </div>
            {(offPieces > 0 || offValue > 0) && (
              <div className="text-[10px] font-normal text-gray-400 mt-0.5">
                Shirting + Suiting (broken down below): {fmtNumber(ssPieces)} pcs · {fmtCurrency(ssValue)}
                {' · '}
                <span title="Mixed Dispatch / Pack-Stock / Unknown / Ethnic — not part of the Shirting/Suiting head-category breakdown but included in the total above.">
                  Other + Ethnic: {fmtNumber(offPieces)} pcs · {fmtCurrency(offValue)}
                </span>
              </div>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {/* 4-tile colored summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {rows.map(r => {
            // Always compute both % pieces and % value from the breakdown
            const b = breakdown[r.name] || {};
            const pctVal = b.pct_value || 0;
            // PYSP + 365d trend metrics (added when include_trends=true)
            const pctPysp = b.pct_value_pysp;
            const pctR365 = b.pct_value_r365;
            const deltaPysp = b.delta_pp_pysp;
            const fmtDelta = (d) => (d == null ? '' : `${d >= 0 ? '↑' : '↓'} ${Math.abs(d).toFixed(1)}pp`);
            const deltaColour = (d) => (d == null ? '' : d >= 0 ? '#15803D' : '#B91C1C');
            return (
              <div
                key={r.name}
                className="rounded-lg p-3 border"
                style={{ borderColor: `${r.color}40`, backgroundColor: `${r.color}0D` }}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color }} />
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: r.color }}>{r.name}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-2 text-[11px]">
                  <div>
                    <div className="text-gray-500 uppercase tracking-wide text-[9px]">Pieces</div>
                    <div className="font-bold text-gray-900 leading-tight">{fmtNumber(r.pieces)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 uppercase tracking-wide text-[9px]">Value</div>
                    <div className="font-bold text-gray-900 leading-tight">{fmtCurrency(r.netValue)}</div>
                    <div className="text-gray-600 leading-tight">{pctVal.toFixed(1)}% share</div>
                    {pctPysp != null && (
                      <div className="text-[9px] mt-0.5" style={{ color: deltaColour(deltaPysp) }} title="Previous-year-same-period share + change in percentage points">
                        PY {pctPysp.toFixed(1)}% {fmtDelta(deltaPysp)}
                      </div>
                    )}
                    {pctR365 != null && (
                      <div className="text-[9px] text-gray-500 mt-0.5" title="Rolling 365-day share">
                        365d {pctR365.toFixed(1)}%
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pie (% share) + Bar (absolute) — complementary, not redundant */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-3 border-t border-gray-100">
          {/* Pie — % share */}
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
              Share of total ({metric === 'value' ? 'by ₹' : 'by metres'})
              <span
                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gray-200 text-gray-600 text-[9px] font-bold cursor-help"
                title="Each slice = one fabric category's share of total dispatched pieces (or ₹). Toggle metric in the toolbar above."
              >i</span>
            </div>
            {(chartType === 'bars' || chartType === 'treemap') ? (
              <ShareChart data={rows} chartType={chartType} height={220} tip={<RechartsTooltip content={<HeadFabricTooltip metric={metric} />} />} />
            ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie isAnimationActive={false}
                  data={rows}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={chartType === 'pie' ? 0 : 50}
                  outerRadius={85}
                  paddingAngle={2}
                  stroke="#fff"
                  strokeWidth={2}
                  label={(p) => `${p.name} ${p.pct?.toFixed(1)}%`}
                  labelLine={{ stroke: '#94A3B8', strokeWidth: 1 }}
                >
                  {rows.map((r, i) => <Cell key={i} fill={r.color} />)}
                </Pie>
                <RechartsTooltip content={<HeadFabricTooltip metric={metric} />} />
              </PieChart>
            </ResponsiveContainer>
            )}
          </div>

          {/* Bar — % pieces vs % value side by side */}
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
              % share — metres vs value
              <span
                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gray-200 text-gray-600 text-[9px] font-bold cursor-help"
                title="Two bars per fabric: % share by pieces and % share by ₹ value. A fabric with high pieces % but low value % is dragging blended realised rate down; the reverse signals a premium driver."
              >i</span>
            </div>
            <div className="flex items-center gap-4 text-[11px] text-gray-600 mb-1">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-2.5 rounded-sm" style={{ backgroundColor: '#3B82F6' }} />
                <span>% by metres</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-2.5 rounded-sm" style={{ backgroundColor: '#F59E0B' }} />
                <span>% by ₹</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barRows} layout="vertical" margin={{ top: 5, right: 50, left: 5, bottom: 5 }} barCategoryGap="20%">
                <CartesianGrid horizontal={false} stroke="#F1F5F9" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: '#64748B' }}
                  unit="%"
                  domain={[0, 'auto']}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#475569', fontWeight: 600 }}
                  width={75}
                  interval={0}
                />
                <RechartsTooltip
                  cursor={{ fill: '#F8FAFC' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2.5 text-sm">
                        <div className="font-semibold text-gray-800 mb-1.5">{d.name}</div>
                        <div className="space-y-0.5 text-xs">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#3B82F6' }} />
                              <span className="text-gray-600">% by metres</span>
                            </div>
                            <span className="font-semibold text-gray-900">{d.pctPcs.toFixed(1)}%</span>
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#F59E0B' }} />
                              <span className="text-gray-600">% by ₹</span>
                            </div>
                            <span className="font-semibold text-gray-900">{d.pctVal.toFixed(1)}%</span>
                          </div>
                          <div className="border-t border-gray-100 pt-1 mt-1 text-[11px] text-gray-500">
                            {fmtNumber(d.pieces)} pcs · {fmtCurrency(d.netValue)}
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
                <Bar isAnimationActive={false} dataKey="pctPcs" fill="#3B82F6" radius={[0, 3, 3, 0]}>
                  <LabelList dataKey="pctPcs" position="right"
                    formatter={(v) => `${Number(v).toFixed(1)}%`}
                    style={{ fontSize: 9, fill: '#1E40AF', fontWeight: 600 }} />
                </Bar>
                <Bar isAnimationActive={false} dataKey="pctVal" fill="#F59E0B" radius={[0, 3, 3, 0]}>
                  <LabelList dataKey="pctVal" position="right"
                    formatter={(v) => `${Number(v).toFixed(1)}%`}
                    style={{ fontSize: 9, fill: '#92400E', fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Stacks all four fabric buckets in order — only renders if `breakdown` exists.
const FabricStack = ({ breakdown, dispatchTrades, orderTrades, metric }) => {
  if (!breakdown) return null;
  return (
    <div className="space-y-4">
      {/* Head category overview ALWAYS first — pie/bar summary of fabrics */}
      <HeadFabricCategoryCard breakdown={breakdown} metric={metric} />
      {FABRIC_ORDER.map(fabric => (
        <FabricBucketCard
          key={fabric}
          fabric={fabric}
          summary={breakdown[fabric]}
          dispatchTrades={dispatchTrades}
          orderTrades={orderTrades}
          metric={metric}
        />
      ))}
    </div>
  );
};

// ─── Composition Cards (pie + bar) ──────────────────────────
const GAP_PP = 3;  // an ordered-vs-dispatched gap smaller than this is noise
// Mix-bale / pack labels are never in the orders collection (bale
// authorisations live in the Mix Bales register), so they are kept out of
// the ordered-vs-dispatched comparison.
const NON_ORDER_LABELS = new Set(['Mix Bale Shirting', 'Mix Bale Suiting', 'Mix Bale Combo Pack', 'Mixed Dispatch (Unassigned)', 'Pack/Stock Dispatch', 'Unknown']);

const CompositionCard = ({ title, icon: Icon, color, summary, trades, orderTrades, metric, qtyLabel, qtyUnit }) => {
  const valueKey = metric === 'value' ? 'pct_value' : 'pct_qty';
  const rawKey = metric === 'value' ? 'value' : 'qty';

  // Top N qualities + Others, where N is the smallest of 8/12/20/30/50 that
  // keeps Others under a quarter of the total — one big grey "Others (165)"
  // slice said nothing. Ranked by the metric on display.
  const { chartType, topChoice } = useContext(ChartPrefs);
  const pieData = useMemo(() => {
    const ranked = [...trades].filter(t => (t[rawKey] || 0) > 0).sort((a, b) => (b[rawKey] || 0) - (a[rawKey] || 0));
    const total = ranked.reduce((s, t) => s + (t[rawKey] || 0), 0);
    let top = ranked.length;
    if (topChoice === 'auto') {
      for (const n of [8, 12, 20, 30, 50]) {
        const others = ranked.slice(n).reduce((s, t) => s + (t[rawKey] || 0), 0);
        if (total > 0 && others / total <= 0.25) { top = n; break; }
      }
    } else if (topChoice !== 'all') {
      top = parseInt(topChoice, 10) || 12;
    }
    const shown = ranked.slice(0, top);
    const rest = ranked.slice(top);
    const out = shown.map((t, i) => ({
      name: t.trade_name,
      value: t[rawKey],
      pct: t[valueKey],
      qtyUnit,
      color: TRADE_COLORS[i % TRADE_COLORS.length],
    }));
    if (rest.length > 0) {
      const restValue = rest.reduce((s, t) => s + t[rawKey], 0);
      const restPct = rest.reduce((s, t) => s + t[valueKey], 0);
      out.push({ name: `Others (${rest.length})`, value: restValue, pct: restPct, qtyUnit, color: '#CBD5E1' });
    }
    return out;
  }, [trades, valueKey, rawKey, qtyUnit, topChoice]);

  // The "Product mix" chart in the chosen shape. Not a hook.
  const renderMixChart = () => {
    const tip = <RechartsTooltip content={<PieTip metric={metric} />} />;
    if (chartType === 'bars') {
      return (
        <ResponsiveContainer width="100%" height={Math.max(220, pieData.length * 24 + 30)}>
          <BarChart data={pieData} layout="vertical" margin={{ top: 4, right: 60, left: 4, bottom: 4 }}>
            <CartesianGrid horizontal={false} stroke="#F1F5F9" />
            <XAxis type="number" tick={{ fontSize: 10, fill: '#64748B' }} unit="%" domain={[0, 'auto']} dataKey="pct" />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} width={150} interval={0} />
            {tip}
            <Bar isAnimationActive={false} dataKey="pct" radius={[0, 3, 3, 0]}>
              {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
              <LabelList dataKey="pct" position="right" formatter={(v) => `${Number(v).toFixed(1)}%`} style={{ fontSize: 9, fill: '#334155', fontWeight: 600 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }
    if (chartType === 'treemap') {
      const TreeCell = ({ x, y, width, height, name, pct, color }) => {
        if (width == null || height == null) return null;
        const roomy = width > 64 && height > 30;
        const maxChars = Math.max(3, Math.floor(width / 6.5));
        return (
          <g>
            <rect x={x} y={y} width={width} height={height} rx={3} fill={color || '#CBD5E1'} stroke="#fff" strokeWidth={2} />
            {roomy && <text x={x + 5} y={y + 14} fontSize={10} fontWeight={600} fill="#fff">{(name || '').length > maxChars ? `${(name || '').slice(0, maxChars - 1)}…` : name}</text>}
            {roomy && height > 44 && pct != null && <text x={x + 5} y={y + 27} fontSize={9} fill="rgba(255,255,255,0.9)">{Number(pct).toFixed(1)}%</text>}
          </g>
        );
      };
      return (
        <ResponsiveContainer width="100%" height={300}>
          <Treemap data={pieData.map(e => ({ ...e, size: e.value }))} dataKey="size" nameKey="name" aspectRatio={4 / 3} isAnimationActive={false} content={<TreeCell />}>
            {tip}
          </Treemap>
        </ResponsiveContainer>
      );
    }
    const donut = chartType !== 'pie';
    return (
      <div className="flex items-center gap-3">
        <ResponsiveContainer width="55%" height={260}>
          <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <Pie isAnimationActive={false}
              data={pieData}
              dataKey="value"
              nameKey="name"
              innerRadius={donut ? 38 : 0}
              outerRadius={70}
              paddingAngle={donut ? 1.5 : 0}
              stroke="#fff"
              strokeWidth={1.5}
              label={(p) => p.pct >= 4 ? `${p.name.length > 10 ? p.name.substring(0, 10) + '…' : p.name} ${p.pct.toFixed(1)}%` : ''}
              labelLine={{ stroke: '#94A3B8', strokeWidth: 0.8 }}
            >
              {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
            </Pie>
            {tip}
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
          {pieData.map((e, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: e.color }} />
                <span className="truncate text-gray-700">{e.name}</span>
              </div>
              <span className="font-semibold text-gray-800 flex-shrink-0">{e.pct.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Bar plots BOTH % qty (pieces / units) and % value (₹) per trade, so an
  // analyst can spot imbalances: a trade that's high in qty but low in
  // value (basic / low-rate SKU) reads differently from one that's low in
  // qty but high in value (premium SKU). Top 10 ranking uses max(% qty,
  // % value) so notable trades on either axis make the cut.
  // As many qualities as the Product mix chart shows (the Show switch).
  const shownCount = pieData.filter(e => !e.name.startsWith('Others (')).length;
  const barData = useMemo(() => {
    const ranked = [...trades]
      .map(t => ({ ...t, _maxPct: Math.max(t.pct_qty || 0, t.pct_value || 0) }))
      .sort((a, b) => b._maxPct - a._maxPct)
      .slice(0, shownCount);
    return ranked.map((t, i) => ({
      name: t.trade_name.length > 14 ? t.trade_name.substring(0, 14) + '…' : t.trade_name,
      fullName: t.trade_name,
      pctQty: t.pct_qty || 0,
      pctValue: t.pct_value || 0,
      rawQty: t.qty || 0,
      rawValue: t.value || 0,
      qtyUnit,
      // PYSP + 365d for trend comparison (added when include_trends=true)
      valuePysp: t.value_pysp,
      pctValuePysp: t.pct_value_pysp,
      deltaPysp: t.delta_pp_pysp,
      pctValueR365: t.pct_value_r365,
    }));
  }, [trades, qtyUnit, shownCount]);

  const isEmpty = !trades || trades.length === 0;

  // Ordered ahead of dispatched — the one order-side signal kept on the
  // page. Only DEMAND RUNNING AHEAD OF SUPPLY is shown: order coverage is
  // partial (~78% of dispatched pieces have an order copy), so "dispatched
  // ahead of ordered" almost always means the order copy is not in the
  // system, not over-shipping — reporting it misled. Mix bales are excluded
  // (bale authorisations are not orders). Order value is estimated, so this
  // is a pointer, not a figure to quote.
  const demandGap = useMemo(() => {
    if (!orderTrades || !orderTrades.length) return null;
    const oMap = {};
    orderTrades.forEach(t => { oMap[(t.trade_name || '').toLowerCase()] = t; });
    const shownNames = new Set(pieData.filter(e => !e.name.startsWith('Others (')).map(e => e.name.toLowerCase()));
    const rows = [];
    trades.forEach(t => {
      if (NON_ORDER_LABELS.has(t.trade_name)) return;
      const key = (t.trade_name || '').toLowerCase();
      if (!shownNames.has(key)) return;
      const o = oMap[key];
      const disp = t[valueKey] || 0;
      const ord = o ? (o[valueKey] || 0) : 0;
      if (ord - disp >= GAP_PP) rows.push({ name: t.trade_name, disp, ord, gap: ord - disp, dispatched: true });
    });
    // Ordered but never dispatched in the period — the strongest unmet-demand signal.
    const dispNames = new Set(trades.map(t => (t.trade_name || '').toLowerCase()));
    orderTrades.forEach(o => {
      if (NON_ORDER_LABELS.has(o.trade_name)) return;
      const key = (o.trade_name || '').toLowerCase();
      if (dispNames.has(key)) return;
      const ord = o[valueKey] || 0;
      if (ord >= 1) rows.push({ name: o.trade_name, disp: 0, ord, gap: ord, dispatched: false });
    });
    const flagged = rows.sort((a, b) => b.gap - a.gap);
    return { rows, flagged };
  }, [orderTrades, trades, pieData, valueKey]);

  return (
    <Card className="border border-gray-200 shadow-sm">
      <CardHeader className="pb-3 border-b border-gray-100">
        <CardTitle className="flex items-center gap-2 text-base font-semibold text-gray-800">
          <span className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}15`, color }}>
            <Icon size={15} />
          </span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 gap-3 pb-4 border-b border-gray-100">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">{qtyLabel}</div>
            <div className="text-lg font-bold text-gray-900">{fmtNumber(summary.qty)}</div>
            {summary.pieces != null && (
              <div className="text-[11px] text-gray-500 mt-0.5">{fmtNumber(summary.pieces)} pcs</div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Value</div>
            <div className="text-lg font-bold text-gray-900">{fmtCurrency(summary.value)}</div>
          </div>
        </div>

        {isEmpty ? (
          <div className="py-8 text-center text-sm text-gray-500">
            No {title.toLowerCase()} data for this period
          </div>
        ) : (
          <>
            {/* Pie — labels rendered DIRECTLY on slices (≥4% only, to avoid
                clutter), with the side legend retained for the long tail */}
            <div>
              <div className="text-xs font-semibold text-gray-700 mb-2">Product mix</div>
              {renderMixChart()}
            </div>

            {demandGap && (
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1">
                  Ordered ahead of dispatched
                  <span
                    className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gray-200 text-gray-600 text-[9px] font-bold cursor-help"
                    title={`Qualities on the chart whose share of orders runs ${GAP_PP} points or more ahead of their share of dispatches (by ${metric === 'value' ? '₹' : 'quantity'}) — demand not being supplied — plus qualities ordered but not dispatched in the period. The reverse is not shown: order coverage is partial, so dispatched-ahead usually means the order copy is not on file. Mix bales are excluded (bale authorisations are not orders). Order value is estimated, so read this as a pointer.`}
                  >i</span>
                </div>
                {demandGap.flagged.length === 0 ? (
                  <div className="text-[11px] text-gray-500">No quality's ordered share runs {GAP_PP} points or more ahead of its dispatched share.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left py-1 font-medium">Quality</th>
                          <th className="text-right py-1 font-medium">Dispatched</th>
                          <th className="text-right py-1 font-medium">Ordered</th>
                          <th className="text-right py-1 font-medium">Gap</th>
                        </tr>
                      </thead>
                      <tbody>
                        {demandGap.flagged.slice(0, 12).map(r => (
                          <tr key={r.name} className="border-t border-gray-100">
                            <td className="py-1 text-gray-800">
                              {r.name}
                              {!r.dispatched && <span className="ml-1.5 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-semibold">ordered, not dispatched</span>}
                            </td>
                            <td className="py-1 text-right">{r.disp.toFixed(1)}%</td>
                            <td className="py-1 text-right">{r.ord.toFixed(1)}%</td>
                            <td className="py-1 text-right font-semibold text-amber-700">
                              +{r.gap.toFixed(1)} pp · under-supplied
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {demandGap.flagged.length > 12 && <div className="text-[10px] text-gray-400 mt-1">{demandGap.flagged.length - 12} more below the fold — narrow the period to see them.</div>}
                  </div>
                )}
              </div>
            )}

            {/* Grouped bars — % qty AND % value side by side per trade.
                Lets the analyst spot trades that are high in qty but low
                in value (basic SKUs) or low in qty but high in value
                (premium SKUs) — neither metric alone shows the full picture. */}
            <div className="pt-3 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                {shownCount >= trades.length ? `All ${trades.length} qualities` : `Top ${shownCount} qualities`} — % share comparison
                <span
                  className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gray-200 text-gray-600 text-[9px] font-bold cursor-help"
                  title="Two bars per trade: % share by quantity (pieces/units) and % share by ₹ value. A wide gap between them flags imbalances — e.g. high-qty/low-value basics or low-qty/high-value premiums."
                >i</span>
              </div>
              {/* Inline legend */}
              <div className="flex items-center gap-4 text-[11px] text-gray-600 mb-2">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-2.5 rounded-sm" style={{ backgroundColor: '#3B82F6' }} />
                  <span>% by {qtyUnit}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-2.5 rounded-sm" style={{ backgroundColor: '#F59E0B' }} />
                  <span>% by ₹</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={Math.max(220, barData.length * 36 + 40)}>
                <BarChart data={barData} layout="vertical" margin={{ top: 5, right: 50, left: 5, bottom: 5 }} barCategoryGap="20%">
                  <CartesianGrid horizontal={false} stroke="#F1F5F9" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10, fill: '#64748B' }}
                    domain={[0, 'auto']}
                    unit="%"
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 10, fill: '#475569' }}
                    width={100}
                    interval={0}
                  />
                  <RechartsTooltip
                    cursor={{ fill: '#F8FAFC' }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2.5 text-sm">
                          <div className="font-semibold text-gray-800 mb-1.5">{d.fullName}</div>
                          <div className="space-y-0.5 text-xs">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#3B82F6' }} />
                                <span className="text-gray-600">% by {qtyUnit}</span>
                              </div>
                              <span className="font-semibold text-gray-900">{d.pctQty.toFixed(1)}%</span>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#F59E0B' }} />
                                <span className="text-gray-600">% by ₹</span>
                              </div>
                              <span className="font-semibold text-gray-900">{d.pctValue.toFixed(1)}%</span>
                            </div>
                            <div className="border-t border-gray-100 pt-1 mt-1 text-[11px] text-gray-500">
                              {fmtNumber(d.rawQty)} {d.qtyUnit} · {fmtCurrency(d.rawValue)}
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Bar isAnimationActive={false} dataKey="pctQty" fill="#3B82F6" radius={[0, 3, 3, 0]}>
                    <LabelList
                      dataKey="pctQty"
                      position="right"
                      formatter={(v) => `${Number(v).toFixed(1)}%`}
                      style={{ fontSize: 9, fill: '#1E40AF', fontWeight: 600 }}
                    />
                  </Bar>
                  <Bar isAnimationActive={false} dataKey="pctValue" fill="#F59E0B" radius={[0, 3, 3, 0]}>
                    <LabelList
                      dataKey="pctValue"
                      position="right"
                      formatter={(v) => `${Number(v).toFixed(1)}%`}
                      style={{ fontSize: 9, fill: '#92400E', fontWeight: 600 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Trend comparison vs PYSP / 365d is in a dedicated
                'Trend Analysis' tab — see TrendAnalysis.jsx. */}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─── The view switches (₹ / metres, Chart) ──────────────────
// One component so the page bar and the report view drive the same state.
const ViewControls = ({ metric, setMetric, chartType, setChartType }) => (
  <>
    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
      {[{ v: 'qty', l: 'By Metres' }, { v: 'value', l: 'By Value (₹)' }].map(m => (
        <button key={m.v} type="button" onClick={() => setMetric(m.v)}
          className={`text-xs px-3 py-1 rounded-md transition font-medium ${metric === m.v ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>
          {m.l}
        </button>
      ))}
    </div>
    <div className="flex items-center gap-1" title="Shape of every composition chart on the page">
      <span className="text-[10px] text-gray-500 font-medium">Chart</span>
      {PRODUCT_CHART_TYPES.map(c => (
        <button key={c.key} type="button" onClick={() => { setChartType(c.key); writePref('pm_product_chart', c.key); }}
          className={`text-[11px] px-2 py-1 rounded-md transition font-medium ${chartType === c.key ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-600 hover:text-gray-900'}`}>
          {c.label}
        </button>
      ))}
    </div>
  </>
);

// ═══════════════════════════════════════════════════════════
export default function RegionProductMix({ selectedDivision }) {
  const [periodType, setPeriodType] = useState('fy');
  const [fy, setFy] = useState(getCurrentFY());
  const [monthValue, setMonthValue] = useState(`${getCurrentFY()}-04`);
  const [quarter, setQuarter] = useState(1);
  // Default metric is VALUE (₹). Pieces-based pies over-represent
  // bulk pack/stock dispatches (each line is 75-110 pieces but very
  // low realised rate). ₹ value gives the more honest revenue picture.
  // User can still toggle to 'qty' from the toolbar.
  const [metric, setMetric] = useState('value'); // qty | value
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeRegion, setActiveRegion] = useState(null); // null = overview grid
  const [downloading, setDownloading] = useState(false);
  const [subTab, setSubTab] = useState('overall'); // 'overall' | 'regions' | 'compare'
  const [viewer, setViewer] = useState(null); // { scope, label } while the in-app report view is open
  // Account picker: narrows the whole tab (charts, regions, View, PDF) to one
  // customer or store. Remembered with its division so switching division
  // drops it instead of asking for an account the division doesn't have.
  const [acctKind, setAcctKind] = useState('customer');
  const [accountSel, setAccountSel] = useState(null); // { division, kind, key, label }
  const [viewerReports, setViewerReports] = useState([]);   // the View's reports, for its jump chips
  const account = accountSel && accountSel.division === selectedDivision ? accountSel : null;
  const acctOpts = useAccountOptions(selectedDivision, true);
  const acctList = useMemo(() => accountOptions(acctKind, acctOpts), [acctKind, acctOpts]);
  const scopeParams = account ? { scope_kind: account.kind, scope: account.key } : {};
  // Product-mix chart preferences (shared by every CompositionCard on the page)
  const [chartType, setChartType] = useState(() => readPref('pm_product_chart', 'donut'));
  // Quality charts size themselves (Auto: the rest fold into Others once they
  // are under a quarter). A fixed Top N was dropped — across regions it mixed
  // different markets into one ranking; the Breakdown tab answers that instead.
  const topChoice = 'auto';
  // A sales member's Product Mix covers only their own customers (server-scoped).
  const memberView = !!data?.member_view;
  const chartPrefs = useMemo(() => ({ chartType, topChoice, division: selectedDivision, memberView }), [chartType, selectedDivision, memberView]);

  const periodValue = useMemo(() => {
    if (periodType === 'month') return monthValue;
    if (periodType === 'quarter') return `${fy}-Q${quarter}`;
    return String(fy);
  }, [periodType, monthValue, quarter, fy]);

  const periodLabel = useMemo(() => {
    if (periodType === 'month') {
      const opt = fyMonthOptions(Math.floor(parseInt(monthValue.split('-')[0]) - (parseInt(monthValue.split('-')[1]) < 4 ? 1 : 0))).find(o => o.v === monthValue);
      return opt?.l || monthValue;
    }
    if (periodType === 'quarter') return `${QUARTERS.find(q => q.v === quarter)?.l} · ${fyLabel(fy)}`;
    return fyLabel(fy);
  }, [periodType, monthValue, quarter, fy]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/region-product-mix`, {
        params: {
          division: selectedDivision !== 'all' ? selectedDivision : undefined,
          period_type: periodType,
          period_value: periodValue,
          ...scopeParams,
        },
        withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      const msg = errorText(e) || 'Failed to load region × product analytics';
      toast.error(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedDivision, periodType, periodValue, account?.kind, account?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset drilldown when period or filter changes
  useEffect(() => { setActiveRegion(null); }, [selectedDivision, periodType, periodValue, account?.key]);

  // View opens the report as an interactive page inside the app; Download
  // saves the PDF.
  const downloadPdf = useCallback(async (scope = 'overall') => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/analytics/region-product-mix/pdf`, {
        params: {
          division: selectedDivision !== 'all' ? selectedDivision : undefined,
          period_type: periodType,
          period_value: periodValue,
          region: scope,
          ...scopeParams,
        },
        responseType: 'blob',
        withCredentials: true,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      const fname = match?.[1] || `ProductMix_${periodValue}.pdf`;
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      // blob errors don't expose JSON detail directly — try to read it
      let detail = 'Download failed';
      if (e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail || detail; } catch {}
      } else {
        detail = e?.response?.data?.detail || detail;
      }
      toast.error(detail);
    } finally {
      setDownloading(false);
    }
  }, [selectedDivision, periodType, periodValue, account?.kind, account?.key]); // eslint-disable-line react-hooks/exhaustive-deps



  const regions = data?.regions || [];
  const overall = data?.overall || null;

  const drilldownRegion = useMemo(() => {
    if (!activeRegion) return null;
    return regions.find(r => r.region === activeRegion) || null;
  }, [activeRegion, regions]);

  return (
    <ChartPrefs.Provider value={chartPrefs}>
    <div className="space-y-5">
      {memberView && (
        <div className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-xs text-teal-900">
          <span className="font-semibold">Your accounts only.</span> Every figure, chart, compare and report here covers the customers in your regions and divisions, plus those assigned to you — not the whole division.
        </div>
      )}
      {/* Top control bar */}
      <Card className="border border-gray-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          {/* Period type */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {[
              { v: 'month', l: 'Month' },
              { v: 'quarter', l: 'Quarter' },
              { v: 'fy', l: 'FY' },
            ].map(p => (
              <button
                key={p.v}
                onClick={() => setPeriodType(p.v)}
                className={`text-xs px-3 py-1 rounded-md transition font-medium ${
                  periodType === p.v ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {p.l}
              </button>
            ))}
          </div>

          {/* Period selectors */}
          {periodType === 'month' && (
            <>
              <Select value={String(fy)} onValueChange={(v) => { setFy(parseInt(v)); setMonthValue(`${v}-04`); }}>
                <SelectTrigger className="h-9 text-sm w-[140px] bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FY_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={monthValue} onValueChange={setMonthValue}>
                <SelectTrigger className="h-9 text-sm w-[150px] bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {fyMonthOptions(fy).map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          {periodType === 'quarter' && (
            <>
              <Select value={String(fy)} onValueChange={(v) => setFy(parseInt(v))}>
                <SelectTrigger className="h-9 text-sm w-[140px] bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FY_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={String(quarter)} onValueChange={(v) => setQuarter(parseInt(v))}>
                <SelectTrigger className="h-9 text-sm w-[160px] bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {QUARTERS.map(q => <SelectItem key={q.v} value={String(q.v)}>{q.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          {periodType === 'fy' && (
            <Select value={String(fy)} onValueChange={(v) => setFy(parseInt(v))}>
              <SelectTrigger className="h-9 text-sm w-[140px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FY_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          {subTab !== 'compare' && (
            <>
              <div className="h-6 w-px bg-gray-200" />
              <div className="flex items-center gap-2" title="Narrow everything on this tab — charts, regions, View and PDF — to one customer or store">
                <span className="text-[10px] text-gray-500 font-medium">Account</span>
                <KindSwitch kinds={ACCOUNT_KINDS} value={acctKind} onChange={setAcctKind} />
                <SidePicker options={acctList} color="#0F766E"
                  value={account && account.kind === acctKind ? account.key : ''}
                  placeholder={`All ${acctKind === 'store' ? 'stores' : 'customers'}`}
                  onChange={(key) => {
                    const o = acctList.find(x => x.key === key);
                    setAccountSel({ division: selectedDivision, kind: acctKind, key, label: o?.label || key });
                  }} />
                {account && (
                  <button type="button" onClick={() => setAccountSel(null)}
                    className="text-[11px] px-2 py-1 rounded-md bg-teal-50 text-teal-800 hover:bg-teal-100 font-medium">
                    ✕ All accounts
                  </button>
                )}
              </div>
            </>
          )}

          <div className="h-6 w-px bg-gray-200" />
          <ViewControls metric={metric} setMetric={setMetric}
            chartType={chartType} setChartType={setChartType} />

          <div className="ml-auto flex items-center gap-2">
            <div className="text-xs text-gray-500 hidden md:block">
              <Badge variant="secondary" className="text-[10px] mr-1.5">{selectedDivision === 'all' ? 'All Divisions' : selectedDivision}</Badge>
              {account && subTab !== 'compare' && <Badge className="text-[10px] mr-1.5 bg-teal-600 hover:bg-teal-600">{account.label}</Badge>}
              {periodLabel}
            </div>
            {(() => {
              // Single context-aware download button. The scope follows what
              // the user is looking at: a specific region drill-down → that
              // region; the Region Breakdown sub-tab → all regions; the
              // Overall sub-tab → division-wide composition.
              let scope = 'overall';
              let label = 'Download PDF';
              let hint = 'Overall composition for the selected period';
              if (activeRegion) {
                scope = activeRegion;
                label = `PDF — ${activeRegion}`;
                hint = `Product composition for ${activeRegion}`;
              } else if (subTab === 'regions') {
                scope = 'all_regions';
                label = 'PDF — All Regions';
                hint = 'Overall + every region in one document';
              } else if (account) {
                // One account → the customer-facing account report (screen
                // and PDF draw the same data), ready to share.
                label = 'Customer report PDF';
                hint = `Account report for ${account.label} — purchases, category split, price ladders, qualities`;
              }
              if (subTab === 'compare' && !activeRegion) return null;
              return (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setViewerReports([]); setViewer({ scope, label: label.replace(/^PDF — /, '').replace('Download PDF', 'Overall') }); }}
                    disabled={loading || !data}
                    className="h-9 text-xs gap-1.5"
                    title={`Open as an interactive report — ${hint}`}
                  >
                    <Eye size={14} />
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => downloadPdf(scope)}
                    disabled={!!downloading || loading}
                    className="h-9 text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700"
                    title={hint}
                  >
                    {downloading
                      ? <Loader2 size={14} className="animate-spin" />
                      : <FileDown size={14} />}
                    {label}
                  </Button>
                </>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500"></div>
        </div>
      ) : !data || (regions.length === 0 && (!overall || (!overall.dispatches?.total_metres && !overall.dispatches?.total_pieces))) ? (
        <Card className="border border-gray-200">
          <CardContent className="py-16 text-center">
            <div className="text-gray-400 mb-2"><Layers size={32} className="mx-auto" /></div>
            <p className="text-gray-600 font-medium">No order or dispatch data{account ? ` for ${account.label}` : ''} in {periodLabel}</p>
            <p className="text-xs text-gray-500 mt-1">Try a wider period or a different division.</p>
          </CardContent>
        </Card>
      ) : drilldownRegion ? (
        // ── Drill-down: single region ──
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => setActiveRegion(null)} className="gap-1.5">
              <ChevronLeft size={14} /> All Regions
            </Button>
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <MapPin size={18} className="text-indigo-500" /> {drilldownRegion.region}
              </h2>
              <p className="text-xs text-gray-500">{periodLabel}</p>
            </div>
          </div>

          {/* TAS-only: fabric-type stacked breakdown */}
          {drilldownRegion.fabric_breakdown ? (
            <FabricStack
              breakdown={drilldownRegion.fabric_breakdown}
              dispatchTrades={drilldownRegion.dispatches?.trades || []}
              orderTrades={drilldownRegion.orders?.trades || []}
              metric={metric}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4">
              <CompositionCard
                title="Dispatches"
                icon={Layers}
                color="#16A34A"
                orderTrades={drilldownRegion.orders?.trades || []}
                summary={{
                  qty: drilldownRegion.dispatches?.total_metres || 0,
                  pieces: drilldownRegion.dispatches?.total_pieces || 0,
                  value: drilldownRegion.dispatches?.total_net_value || 0,
                  tradeCount: drilldownRegion.dispatches?.trade_count || 0,
                  metres: drilldownRegion.dispatches?.total_metres || 0,
                }}
                trades={drilldownRegion.dispatches?.trades || []}
                metric={metric}
                qtyLabel="Metres Dispatched"
                qtyUnit="m"
              />
            </div>
          )}
        </div>
      ) : (
        // ── Overview: All regions + Overall composition ──
        <Tabs value={subTab} onValueChange={setSubTab} className="space-y-4">
          <TabsList className="inline-flex h-9 bg-gray-100 rounded-lg p-1 gap-0.5">
            <TabsTrigger value="overall" className="text-xs px-3 py-1 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Overall composition
            </TabsTrigger>
            <TabsTrigger value="regions" className="text-xs px-3 py-1 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Breakdown — region / store / customer
            </TabsTrigger>
            <TabsTrigger value="compare" className="text-xs px-3 py-1 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Compare two
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overall" className="space-y-4">
            {/* TAS-only: fabric-type stacked breakdown */}
            {overall?.fabric_breakdown ? (
              <FabricStack
                breakdown={overall.fabric_breakdown}
                dispatchTrades={overall.dispatches?.trades || []}
                orderTrades={overall.orders?.trades || []}
                metric={metric}
              />
            ) : (
              <div className="grid grid-cols-1 gap-4">
                <CompositionCard
                  title="Dispatches"
                  icon={Layers}
                  color="#16A34A"
                  orderTrades={overall?.orders?.trades || []}
                  summary={{
                    qty: overall?.dispatches?.total_metres || 0,
                    pieces: overall?.dispatches?.total_pieces || 0,
                    value: overall?.dispatches?.total_net_value || 0,
                    tradeCount: overall?.dispatches?.trade_count || 0,
                    metres: overall?.dispatches?.total_metres || 0,
                  }}
                  trades={overall?.dispatches?.trades || []}
                  metric={metric}
                  qtyLabel="Metres Dispatched"
                  qtyUnit="m"
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value="regions" className="space-y-3">
            <ProductMixBreakdown
              selectedDivision={selectedDivision}
              periodType={periodType}
              periodValue={periodValue}
              periodLabel={periodLabel}
              metric={metric}
              account={account}
              onOpenRegion={(name) => setActiveRegion(name)}
              onOpenAccount={(acc, label) => {
                setAcctKind(acc.kind);
                setAccountSel({ division: selectedDivision, kind: acc.kind, key: acc.key, label });
                setSubTab('overall');
              }}
            />
          </TabsContent>

          <TabsContent value="compare" className="space-y-3">
            <ProductMixCompare
              selectedDivision={selectedDivision}
              periodType={periodType}
              periodValue={periodValue}
              periodLabel={periodLabel}
              metric={metric}
              topChoice={topChoice}
              regionNames={regions.map(r => r.region)}
            />
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={!!viewer} onOpenChange={(o) => { if (!o) setViewer(null); }}>
        <DialogContent className="max-w-[96vw] w-[96vw] h-[94vh] p-0 flex flex-col gap-0 overflow-hidden">
          {viewer && (
            <>
              <DialogHeader className="px-6 pt-4 pb-3 border-b border-gray-200 bg-white space-y-2">
                <div className="flex items-center gap-3 pr-8">
                  <DialogTitle className="text-base flex-1 truncate">
                    {account ? `Account report — ${account.label}` : `Product Mix — ${viewer.label}`}
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      {selectedDivision === 'all' ? 'All Divisions' : selectedDivision} · {periodLabel}
                    </span>
                  </DialogTitle>
                  <Button size="sm" onClick={() => downloadPdf(viewer.scope)} disabled={downloading}
                    className="h-8 text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700">
                    {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Download PDF
                  </Button>
                </div>
                {viewerReports.length > 1 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {viewerReports.map((r, i) => (
                      <button key={i} type="button"
                        onClick={() => document.getElementById(`pm-report-${i}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        className="text-[11px] px-2.5 py-0.5 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-indigo-50 hover:border-indigo-300">
                        {r.account?.label}
                      </button>
                    ))}
                  </div>
                )}
              </DialogHeader>
              <div className="flex-1 overflow-y-auto bg-slate-50 px-6 py-6 space-y-10">
                {viewer.scope === 'all_regions' && !account && (
                  <section className="max-w-[1500px] mx-auto space-y-3">
                    <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Breakdown</h3>
                    <ProductMixBreakdown
                      selectedDivision={selectedDivision} periodType={periodType} periodValue={periodValue}
                      periodLabel={periodLabel} metric={metric} account={account}
                      onOpenRegion={(name) => document.getElementById(`pm-report-${viewerReports.findIndex(r => r.account?.label === name)}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      onOpenAccount={(acc, label) => {
                        setViewer(null);
                        setAcctKind(acc.kind);
                        setAccountSel({ division: selectedDivision, kind: acc.kind, key: acc.key, label });
                        setSubTab('overall');
                      }}
                    />
                  </section>
                )}
                <ProductMixAccountReport selectedDivision={selectedDivision} periodType={periodType}
                  periodValue={periodValue} account={account} region={viewer.scope}
                  onLoaded={setViewerReports} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </ChartPrefs.Provider>
  );
}
